/**
 * **Invite to this board** — the board people dialog (spec M1-B1 §7.7, M7-C4, M0-F8 §5.5).
 *
 * Rebuilt in round 9q from the capture the first version did not have:
 * `discovery/ui-reference/subscribers/03-board-members.{png,json}` and
 * `discovery/ui-reference/chrome/board-invite.json` (the same dialog with monday's own chrome
 * unscrubbed, and the hover state of the one row). Every number in `subscribers.css` is read
 * off those two files; the table is in `tasks/T-9q.md` §0.
 *
 *   modal   605 x 650 at (418, 125), radius 16, `rgba(0,0,0,.3) 0 15px 50px`
 *   header  62 tall, `h2` 24/600 at (450, 149), close 32 x 32 at (971, 145)
 *   search  541 x 35 at (450, 203), radius 3 — placeholder *"Search by name, team, or email address"*
 *   hint    y 262, *"Only invited people can find this board"* (private / shareable boards only)
 *   section *"People invited to this board"* 16/700 at y 306; the list starts at y 352 and is
 *           flush with the modal's bottom edge, so it — not the modal — is what scrolls
 *   row     54 tall: avatar 32 at x 458, name 14/400, job title 12/400 secondary, and two
 *           32 x 32 buttons at x 911 (`ownership-button-<id>`) and x 951 (`unsubscribe-button-<id>`)
 *
 * What is **not** monday's: the crown glyph (`@vibe/icons` has none) and the small *Owner*
 * caption, which the capture's single-owner board could not show. What is deliberately absent:
 * monday's *"give feedback"* button at (931, 145), which is monday chrome (§3.2).
 *
 * The old *"In this workspace"* list is gone. People who are not on the board are reached
 * through the search, which is monday's person picker: it filters the list **and** opens a
 * dropdown of account users and teams who are not on it, with an *"Invite … to the account and
 * this board"* row when what was typed is an address nobody owns.
 *
 * Behaviour is unchanged (S1/S2): `add_users_to_board` takes at most 200 ids, a role change is
 * `update_users_board_role`, and each write logs `subscribe` / `unsubscribe` with
 * `item_type: "Board"`. Permissions are unchanged too — the crown, the ×, and the invite row
 * are rendered for board owners and account admins only, and the service checks again anyway.
 */
import { Icon, Tooltip, icons, statusColor, useOverlayDismiss, useToast } from '@elizmonday/ui';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { useBootstrap } from '../shell/bootstrap';
import { initialsOf } from '../board-view/columns/primitives';
import {
  useAccountPeopleSearch,
  useBoard,
  useBoardMembers,
  useBoardMutations,
  useSubscriberPickerMutations,
  useTeamSearch,
} from './hooks';
import {
  LAST_OWNER_TOOLTIP,
  SEARCH_PLACEHOLDER,
  canManageSubscribers,
  inviteRoleFor,
  isAddress,
  orderSubscribers,
  showsFindabilityHint,
} from './subscribers';
import './boards.css';

/** `update_users_board_role(user_ids:)` — max 200 `[sdl]`. */
export const SUBSCRIBER_BATCH_LIMIT = 200;

type PickerRow =
  | { kind: 'user'; id: string; name: string; secondary: string }
  | { kind: 'team'; id: string; name: string; secondary: string }
  | { kind: 'invite'; id: 'invite'; name: string; secondary: string; email: string };

export function BoardSubscribersDialog({ boardId, onClose }: { boardId: string; onClose(): void }) {
  const board = useBoard(boardId);
  const members = useBoardMembers(boardId);
  const bootstrap = useBootstrap();
  const mutations = useBoardMutations();
  const picker = useSubscriberPickerMutations(boardId);
  const toast = useToast();

  const [query, setQuery] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const searchRow = useRef<HTMLDivElement>(null);

  /** P-013: Escape closes, and focus goes back to whatever opened the dialog. */
  useOverlayDismiss({ onClose });

  /*
   * …unless the opener is gone by then. The board permissions dialog's *+ Add* opens this one
   * by **replacing** itself (`BoardHeader`'s single `dialog` state), so the button that opened
   * this is unmounted and `useOverlayDismiss` — which refuses to focus a disconnected node —
   * correctly leaves focus on `<body>`. Round 9q's sweep reported that as `focus-lost` on both
   * phones. The board header's own Invite control is the honest destination: it is what this
   * dialog belongs to, and it is on screen in every case where the opener was not.
   */
  useEffect(
    () => () => {
      setTimeout(() => {
        if (document.activeElement !== document.body) return;
        const fallback =
          document.querySelector<HTMLElement>('[data-testid="board-invite"]') ??
          document.querySelector<HTMLElement>('[data-testid="board-owners"]') ??
          document.querySelector<HTMLElement>('[data-testid="board-menu-toggle"]');
        fallback?.focus();
      }, 0);
    },
    [],
  );

  const me = bootstrap.data?.me ?? null;
  const kind = board.data?.kind ?? 'public';
  const term = query.trim();
  const needle = term.toLowerCase();

  /* ── who is on the board ────────────────────────────────────────────────── */

  const onBoard = useMemo(
    () => (members.data ?? []).filter((member) => member.role !== 'workspace'),
    [members.data],
  );
  const owners = onBoard.filter((member) => member.role === 'owner');

  const canManage = canManageSubscribers(me === null ? null : { id: String(me.id), role: me.role }, onBoard);
  /** F8 §5.3: inviting is an account capability on top of managing this board. */
  const canInvite = canManage && (bootstrap.data?.capabilities.inviteMembers ?? false);

  const matches = (member: { name: string; email: string | null; title: string | null }) =>
    needle === '' ||
    member.name.toLowerCase().includes(needle) ||
    (member.email ?? '').toLowerCase().includes(needle) ||
    (member.title ?? '').toLowerCase().includes(needle);

  const rows = orderSubscribers(onBoard.filter(matches));

  /* ── the typeahead ──────────────────────────────────────────────────────── */

  const people = useAccountPeopleSearch(query, pickerOpen && canManage);
  const teams = useTeamSearch(query, pickerOpen && canManage);

  const pickerRows: PickerRow[] = useMemo(() => {
    if (!pickerOpen || term === '' || !canManage) return [];
    const onBoardIds = new Set(onBoard.map((member) => member.id));
    const known = new Set(onBoard.map((member) => (member.email ?? '').toLowerCase()));
    const found: PickerRow[] = [];
    for (const row of people.data?.rows ?? []) {
      known.add(row.email.toLowerCase());
      if (onBoardIds.has(row.id) || row.isDeleted) continue;
      /* F8 §5.5: a guest can only ever reach a `share` board, so offering one anywhere else
         would add a subscriber who cannot open what they were added to. */
      if (row.role === 'guest' && kind !== 'share') continue;
      found.push({ kind: 'user', id: row.id, name: row.name, secondary: row.email });
    }
    for (const team of teams.data ?? []) {
      if (team.isDeleted || team.disabled || team.memberCount === 0) continue;
      found.push({
        kind: 'team',
        id: team.id,
        name: team.name,
        secondary: `Team · ${String(team.memberCount)} member${team.memberCount === 1 ? '' : 's'}`,
      });
    }
    const shown = found.slice(0, 20);
    if (canInvite && isAddress(term) && !known.has(term.toLowerCase())) {
      shown.push({
        kind: 'invite',
        id: 'invite',
        name: `Invite ${term} to the account and this board`,
        secondary: inviteRoleFor(kind) === 'guest' ? 'As a guest' : 'As a member',
        email: term,
      });
    }
    return shown;
  }, [pickerOpen, term, canManage, canInvite, kind, onBoard, people.data, teams.data]);

  useEffect(() => {
    setActive(0);
  }, [term]);

  const closePicker = () => {
    setPickerOpen(false);
    setActive(0);
  };

  const addUser = (id: string, name: string) => {
    mutations.setSubscribers.mutate({ mode: 'add', id: boardId, userIds: [id], role: 'subscriber' });
    toast(`${name} was added to the board`, { kind: 'positive' });
    setQuery('');
    closePicker();
  };

  const choose = (row: PickerRow) => {
    setInviteError(null);
    if (row.kind === 'user') {
      addUser(row.id, row.name);
      return;
    }
    if (row.kind === 'team') {
      picker.addTeam.mutate(row.id, {
        onSuccess: (result) => {
          toast(`${row.name} was added to the board`, { kind: 'positive' });
          if (result.added === 0) setInviteError('That team has no members.');
        },
        onError: (error: unknown) => setInviteError(messageOf(error)),
      });
      setQuery('');
      closePicker();
      return;
    }
    /* One call: the service invites **and** subscribes, so there is no half-done state to
       repair if the second half fails (T-9q, `services/invitations.ts`). */
    picker.inviteToBoard.mutate(
      { email: row.email, role: inviteRoleFor(kind) },
      {
        onSuccess: (result) => {
          const failure = result.errors[0];
          if (failure) {
            setInviteError(failure.message);
            return;
          }
          toast(`${row.email} was invited to the board`, { kind: 'positive' });
          setQuery('');
          closePicker();
        },
        onError: (error: unknown) => setInviteError(messageOf(error)),
      },
    );
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    /*
     * Escape closes the **dropdown** only while there is one on screen; otherwise it belongs to
     * the dialog. The first version tested `pickerOpen`, which the search's own `autoFocus`
     * turns on the moment the dialog opens — so the very first Escape was swallowed by a
     * dropdown that was not rendered and the dialog could not be closed from the keyboard at
     * all. Round 9q's sweep caught it on both phones (`overlay-not-dismissible`).
     */
    if (event.key === 'Escape' && pickerRows.length > 0) {
      // The dialog's own Escape listener skips a handled event (`useOverlayDismiss`).
      event.preventDefault();
      closePicker();
      return;
    }
    if (pickerRows.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActive((index) => (index + 1) % pickerRows.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => (index - 1 + pickerRows.length) % pickerRows.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = pickerRows[active];
      if (row) choose(row);
    }
  };

  /* ── the row controls ───────────────────────────────────────────────────── */

  const lastOwner = owners.length <= 1;

  const setRole = (userId: string, role: 'owner' | 'subscriber') => {
    mutations.setSubscribers.mutate({ mode: 'setRole', id: boardId, userIds: [userId], role });
  };

  return (
    <div className="ez-dialog-backdrop ez-subscribers-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="ez-subscribers"
        role="dialog"
        aria-modal="true"
        aria-label="Invite to this board"
        data-testid="subscribers-dialog"
        onMouseDown={(event: MouseEvent<HTMLDivElement>) => {
          event.stopPropagation();
          /* A click anywhere but the search row dismisses the dropdown, the way monday's
             person picker does — `onBlur` cannot, because the click that chooses a row is
             itself a blur. */
          if (!searchRow.current?.contains(event.target as Node)) closePicker();
        }}
      >
        <header className="ez-subscribers__header">
          <h2 className="ez-subscribers__title">Invite to this board</h2>
          <Tooltip content="Close">
            <button
              type="button"
              className="ez-subscribers__icon-button ez-subscribers__close"
              aria-label="close"
              data-testid="subscribers-close"
              onClick={onClose}
            >
              <Icon icon={icons.close} size={20} ignoreFocusStyle />
            </button>
          </Tooltip>
        </header>

        <div className="ez-subscribers__body">
          <div className="ez-subscribers__search-row" ref={searchRow}>
            <input
              className="ez-subscribers__search"
              type="text"
              autoFocus
              autoComplete="off"
              aria-label={SEARCH_PLACEHOLDER}
              placeholder={SEARCH_PLACEHOLDER}
              data-testid="subscribers-search"
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPickerOpen(true);
                setInviteError(null);
              }}
              onFocus={() => setPickerOpen(true)}
              onKeyDown={onSearchKeyDown}
            />
            {pickerRows.length > 0 ? (
              <ul className="ez-subscribers__picker" role="listbox" data-testid="subscribers-picker">
                {pickerRows.map((row, index) => (
                  <li key={`${row.kind}-${row.id}`} role="presentation">
                    <button
                      type="button"
                      role="option"
                      aria-selected={index === active}
                      className={`ez-subscribers__picker-row${index === active ? ' is-active' : ''}`}
                      data-testid={`subscribers-picker-${row.kind}-${row.id}`}
                      onMouseEnter={() => setActive(index)}
                      onClick={() => choose(row)}
                    >
                      <span
                        className={`ez-subscribers__picker-avatar${row.kind === 'invite' ? ' is-invite' : ''}`}
                        aria-hidden="true"
                      >
                        {row.kind === 'invite' ? (
                          <Icon icon={icons.email} size={16} ignoreFocusStyle />
                        ) : (
                          initialsOf(row.name)
                        )}
                      </span>
                      <span className="ez-subscribers__picker-text">
                        <span className="ez-subscribers__picker-name">{row.name}</span>
                        <span className="ez-subscribers__picker-secondary">{row.secondary}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            ) : null}
          </div>

          {inviteError === null ? null : (
            <p className="ez-subscribers__error" role="alert" data-testid="subscribers-invite-error">
              {inviteError}
            </p>
          )}

          {showsFindabilityHint(kind) ? (
            <p className="ez-subscribers__hint" data-testid="subscribers-hint">
              <Icon icon={icons.locked} size={16} ignoreFocusStyle />
              Only invited people can find this board
            </p>
          ) : null}

          <h3 className="ez-subscribers__section">People invited to this board</h3>

          <ul className="ez-subscribers__list" data-testid="subscribers-list">
            {rows.map((member) => {
              const isOwner = member.role === 'owner';
              const locked = isOwner && lastOwner;
              return (
                <li key={member.id} className="ez-subscribers__row" data-testid={`subscriber-${member.id}`}>
                  <span
                    className="ez-subscribers__avatar"
                    style={{ backgroundColor: statusColor(member.color.replaceAll('-', '_')) }}
                    aria-hidden="true"
                  >
                    {initialsOf(member.name)}
                  </span>
                  <span className="ez-subscribers__person">
                    <span className="ez-subscribers__name-row">
                      <span className="ez-subscribers__name">{member.name}</span>
                      {isOwner ? <span className="ez-subscribers__owner-tag">Owner</span> : null}
                    </span>
                    {member.title === null || member.title === '' ? null : (
                      <span className="ez-subscribers__job">{member.title}</span>
                    )}
                  </span>
                  {canManage ? (
                    <>
                      <RowButton
                        testid={`subscriber-owner-${member.id}`}
                        label={
                          locked
                            ? LAST_OWNER_TOOLTIP
                            : isOwner
                              ? `Remove ${member.name} as an owner`
                              : `Make ${member.name} an owner`
                        }
                        disabled={locked}
                        onClick={() => setRole(member.id, isOwner ? 'subscriber' : 'owner')}
                      >
                        <Icon icon={isOwner ? icons.crownFilled : icons.crown} size={20} ignoreFocusStyle />
                      </RowButton>
                      <RowButton
                        testid={`subscriber-remove-${member.id}`}
                        label={locked ? LAST_OWNER_TOOLTIP : `Remove ${member.name} from this board`}
                        disabled={locked}
                        onClick={() =>
                          mutations.setSubscribers.mutate({
                            mode: 'remove',
                            id: boardId,
                            userIds: [member.id],
                          })
                        }
                      >
                        <Icon icon={icons.close} size={20} ignoreFocusStyle />
                      </RowButton>
                    </>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}

/** monday's 32 x 32 tertiary icon-button, with the tooltip that names what it does. */
function RowButton({
  testid,
  label,
  disabled,
  onClick,
  children,
}: {
  testid: string;
  label: string;
  disabled: boolean;
  onClick(): void;
  children: ReactNode;
}) {
  return (
    <Tooltip content={label}>
      <button
        type="button"
        className="ez-subscribers__icon-button"
        aria-label={label}
        aria-disabled={disabled}
        disabled={disabled}
        data-testid={testid}
        onClick={onClick}
      >
        {children}
      </button>
    </Tooltip>
  );
}

function messageOf(error: unknown): string {
  if (error instanceof Error && error.message !== '') return error.message;
  return 'That invitation could not be sent.';
}
